import { StateEnum } from '@jerry-serverless/enums/state';

import type { AppData } from '../../types/app_data';
import type { Step } from '../types/step';
import { trackEvent } from '../../utils/analytics';

const TU_STATES: string[] = [
  StateEnum.AK,
  StateEnum.AZ,
  StateEnum.CT,
  StateEnum.FL,
  StateEnum.HI,
  StateEnum.IA,
  StateEnum.ID,
  StateEnum.IL,
  StateEnum.IN,
  StateEnum.MD,
  StateEnum.MN,
  StateEnum.MO,
  StateEnum.NC,
  StateEnum.ND,
  StateEnum.NJ,
  StateEnum.NM,
  StateEnum.NV,
  StateEnum.OH,
  StateEnum.OK,
  StateEnum.PA,
  StateEnum.RI,
  StateEnum.SD,
  StateEnum.TN,
  StateEnum.TX,
  StateEnum.UT,
  StateEnum.VA,
  StateEnum.WA,
  StateEnum.WI,
  StateEnum.WY,
];

/**
 * Check if the user has to pull policy data or not.
 *
 * If it returns boolean, it's the final decision on whether to show the loading step or not.
 * If it returns `null`, other checks should be performed. If no other checks are needed, the loading step should be shown.
 */
export function doesLoadingStepHaveToShowOrNot(appData: AppData): boolean | null {
  if (!appData.selectedService?.insurance) {
    return false;
  }

  // If user is signup from partner link, and has prefilled with drivers and cars, skip loading & LN pulling
  if (appData.signupParams?.partnerSignup && appData.partnerSkippableData?.multiplecarsdrivers === false) {
    return false;
  }

  /**
   * Show loading step if user has violations and he is in TU
   */
  if (appData.signupParams?.violations && TU_STATES.includes(appData.address?.state || '')) {
    return true;
  }

  return null;
}

export const LoadingStep: Step = {
  id: 'loading',
  pathname: '/loading',
  canShow: (appData: AppData) => {
    const haveToPullDataOrNot = doesLoadingStepHaveToShowOrNot(appData);
    if (typeof haveToPullDataOrNot === 'boolean') {
      return haveToPullDataOrNot;
    }

    if (appData.multipleCarsAndDrivers === true) {
      void trackEvent('OnboardingLoadPolicyForSingleCarAndDriver', {
        eventLabel: 'true',
        eventMetadata: { multipleCarsAndDrivers: true },
      });
      return true;
    } else {
      void trackEvent('OnboardingLoadPolicyForSingleCarAndDriver', {
        eventLabel: 'false',
        eventMetadata: { multipleCarsAndDrivers: false },
      });
      return false;
    }
  },
  hasData: () => true,
  hasOwnData: (appData: AppData) => !!appData.searchPolicyData,
  allowBackToSelf: false,
};
